import { Component } from 'react'
import PropTypes from 'prop-types'
import ModalOverlay, {
  ModalOverlayContent,
  ModalOverlayFooter,
} from './ui/shared/overlays/modal-overlay'
import Select from './ui/elements/select'
import Label from './ui/elements/label'
import Button from './ui/elements/button'
import Notice from './ui/shared/notice'

const attributes = {
  lat: 'Latitude',
  lng: 'Longitude',
  z: 'Altitude',
  image: 'Filename',
}

export default class ImagePositionImport extends Component {
  constructor(props) {
    super(props)

    this.state = {
      dragHover: false,
    }

    this.updateFile()
  }

  componentDidUpdate(oldProps) {
    if (
      oldProps.imageFilenames !== this.props.imageFilenames ||
      oldProps.file !== this.props.file
    ) {
      this.updateFile()
    }
  }

  updateFile() {
    const reader = new FileReader()
    reader.addEventListener('loadend', () => {
      const lines = reader.result
        .split('\n')
        .map(line => line.trim())
        .filter((line, i) => i === 0 || !line.startsWith('#'))

      const separator =
        lines[0] && lines[0].indexOf('\t') >= 0
          ? '\t'
          : lines[0].indexOf(',') >= 0
          ? ','
          : ';'

      const data = lines.map(line =>
        line.split(separator).map(col => col.trim())
      )

      const mapping = guessCsvMappings(
        {
          lat: ['latitude', 'lat', 'y', 'northing', 'north'],
          lng: ['longitude', 'lng', 'lon', 'long', 'x', 'easting', 'east'],
          z: ['elevation', 'altitude', 'alt', 'z', 'height'],
          image: ['photo', 'filename', 'file', 'image', 'name'],
        },
        data
      )
      let unmapped = 0
      for (let i = 0; i < mapping.length; i++) {
        if (!mapping[i]) {
          unmapped++
        }
      }

      const skipHeader = unmapped !== mapping.length

      this.setState(
        {
          data,
          skipHeader,
        },
        () => this.updateMapping(mapping)
      )
    })

    reader.readAsText(this.props.file)
  }

  render() {
    // TODO: this logic seems inverted? If we *skip* the header, we shouldn't use the first
    // row as header?
    const columnHeaders = this.state.skipHeader ? this.state.data[0] : []

    return (
      <ModalOverlay
        show={this.props.show}
        size="large"
        title="Import Image Positions"
        onDismiss={() => this.onClose(false)}>
        <ModalOverlayContent>
          <div className="tw-flex tw-flex-col tw-gap-3">
            <h4>Original file</h4>
            {this.state.data && (
              <table className="tw-w-full tw-mb-5 tw-max-w-full tw-text-left tw-text-sm">
                <thead>
                  {this.state.skipHeader ? (
                    <tr>
                      {columnHeaders.map(h => (
                        <th key={h} className="tw-p-2">
                          {h}
                        </th>
                      ))}
                    </tr>
                  ) : (
                    this.state.data[0]?.map((c, i) => (
                      <th key={i} className="tw-p-2">
                        Column {i + 1}
                      </th>
                    ))
                  )}
                </thead>
                <tbody>
                  {this.state.data
                    .slice(this.state.skipHeader ? 1 : 0, 5)
                    .map((row, i) => (
                      <tr className="even:tw-bg-system-grey-50" key={i}>
                        {row.map((col, i) => (
                          <td
                            key={i}
                            className={`tw-border-0 
                          tw-border-t 
                          tw-border-solid 
                          tw-border-system-grey-200  
                          tw-p-2`}>
                            {col}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            )}

            <h4>Columns</h4>
            <div className="tw-my-5 tw-flex tw-flex-col tw-gap-2">
              {this.state.mapping &&
                ['image', 'lat', 'lng', 'z'].map(x => {
                  const columnIndex = this.state.mapping.findIndex(m => m === x)
                  return (
                    <div
                      key={x}
                      className="tw-flex tw-justify-between tw-items-center">
                      <Label htmlFor={`input-col-${x}`}>{attributes[x]}</Label>
                      <div className="tw-w-1/2 ml-3">
                        <Select
                          id={`input-col-${x}`}
                          value={columnIndex}
                          onChange={e => this.onMappingChange(x, e)}>
                          <option value={-1}>No column selected</option>
                          {this.state.mapping.map((mapping, i) => (
                            <option key={i} value={i}>
                              {columnHeaders[i] || `Column ${i + 1}`}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  )
                })}
            </div>

            <h4>Imported result</h4>
            {this.state.mappedData && (
              <table className="tw-w-full tw-mb-5 tw-max-w-full tw-text-left tw-text-sm">
                <thead>
                  <tr>
                    <th className="tw-p-2">{attributes.image}</th>
                    <th className="tw-p-2">{attributes.lng}</th>
                    <th className="tw-p-2">{attributes.lat}</th>
                    <th className="tw-p-2">{attributes.z}</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.mappedData.slice(0, 5).map((row, i) => (
                    <tr className="even:tw-bg-system-grey-50" key={i}>
                      <td
                        className={`tw-border-0 
                          tw-border-t 
                          tw-border-solid 
                          tw-border-system-grey-200  
                          tw-p-2`}>
                        {row.image}
                      </td>
                      <td
                        className={`tw-border-0 
                          tw-border-t 
                          tw-border-solid 
                          tw-border-system-grey-200  
                          tw-p-2`}>
                        {row.lng}
                      </td>
                      <td
                        className={`tw-border-0 
                          tw-border-t 
                          tw-border-solid 
                          tw-border-system-grey-200  
                          tw-p-2`}>
                        {row.lat}
                      </td>
                      <td
                        className={`tw-border-0 
                          tw-border-t 
                          tw-border-solid 
                          tw-border-system-grey-200  
                          tw-p-2`}>
                        {row.z}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(this.state.error && (
              <Notice type="warning">{this.state.error}</Notice>
            )) ||
              (this.state.info && (
                <Notice type="info">{this.state.info}</Notice>
              ))}
          </div>
        </ModalOverlayContent>
        <ModalOverlayFooter>
          <div className="tw-flex tw-justify-between">
            <Button size="medium" onClick={this.onClose.bind(this, false)}>
              Close
            </Button>
            <Button
              size="medium"
              action="primary"
              disabled={this.state.error}
              onClick={this.onClose.bind(this, true)}>
              Save
            </Button>
          </div>
        </ModalOverlayFooter>
      </ModalOverlay>
    )
  }

  onMappingChange(mapping, event) {
    const column =
      event.target.value !== 'not_used' ? event.target.value : undefined
    const nextMapping = [...this.state.mapping]

    if (mapping !== undefined) {
      nextMapping.forEach((m, i) => {
        if (m === mapping) {
          nextMapping[i] = undefined
        }
      })
    }

    nextMapping[column] = mapping

    this.updateMapping(nextMapping)
  }

  updateMapping(nextMapping) {
    const missing = ['image', 'lng', 'lat', 'z'].filter(
      attrib => !nextMapping.find(x => x === attrib)
    )
    const mappedData = mapData(
      nextMapping,
      this.state.skipHeader,
      this.state.data
    )

    const unmappedImages = new Set(this.props.imageFilenames)
    const numberMappedRows = mappedData.reduce(
      (nDeleted, d) => nDeleted + (unmappedImages.delete(d.image) ? 1 : 0),
      0
    )
    const minMappedImages = !this.props.skipMinimumNumberMapped
      ? Math.ceil(this.props.imageFilenames.length * 0.05)
      : 0

    this.setState({
      mapping: nextMapping,
      mappedData,
      error:
        missing.length > 0
          ? `Please define columns for ${missing
              .map(m => attributes[m])
              .join(', ')}.`
          : numberMappedRows < minMappedImages
          ? `Too few images (${numberMappedRows}) georeferenced; at least ${minMappedImages} should be referenced.`
          : null,
      info: `${numberMappedRows} images georeferenced, ${
        unmappedImages.size
      } images are not georeferenced, ${
        mappedData.length - numberMappedRows
      } unused rows.`,
    })
  }

  onClose(save) {
    const imageNames = new Set(this.props.imageFilenames)

    this.props.onClose(
      save,
      save && this.state.mappedData.filter(row => imageNames.has(row.image))
    )
  }
}

const guessCsvMappings = (columnMappings, csv) => {
  const headings = csv[0]
  if (headings[0].startsWith('#')) {
    headings[0] = headings[0].substr(1).trim()
  }
  return headings.map(header =>
    Object.keys(columnMappings).find(key =>
      columnMappings[key].find(m => m === header.trim().toLowerCase())
    )
  )
}

const mapData = (mapping, skipHeader, data) =>
  data.slice(skipHeader ? 1 : 0).map(cols =>
    mapping.reduce((a, m, i) => {
      if (m) {
        a[m] = cols[i]
      }
      return a
    }, {})
  )

ImagePositionImport.propTypes = {
  imageFilenames: PropTypes.array,
  file: PropTypes.string,
  show: PropTypes.bool,
  skipMinimumNumberMapped: PropTypes.bool,
  onClose: PropTypes.func,
}
